import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import { getNews, deleteNews, bulkUpdateNewsStatus, bulkDeleteNews } from '../../services/newsService';
import Pagination from '../../components/Pagination/Pagination';
import './NewsListingPage.css';

const NewsListingPage = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [newsData, setNewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalNews, setTotalNews] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const itemsPerPage = 12;

  const categories = ['Industry News', 'Projects', 'Building Materials', 'Safety', 'Sustainability', 'Technology'];
  const statuses = ['published', 'draft', 'archived'];

  // Fetch news data from API
  const fetchNews = async () => {
    try {
      setLoading(true);
      const params = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
      };
      
      if (searchQuery) params.search = searchQuery;
      if (selectedCategory) params.category = selectedCategory;
      if (selectedStatus) params.status = selectedStatus;
      if (selectedSubCategory) params.subCategory = selectedSubCategory;
      
      const response = await getNews(params);
      setNewsData(response.news || []);
      setTotalNews(response.total || 0);
      setError(null);
    } catch (err) {
      setError('Failed to fetch news');
      console.error('Error fetching news:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, [currentPage, selectedCategory, selectedStatus, selectedSubCategory]);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionMenuOpen && !e.target.closest('.nl-td-action')) {
        setActionMenuOpen(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [actionMenuOpen]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        fetchNews();
      } else {
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === newsData.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(newsData.map(item => item.id));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this news article?')) {
      try {
        await deleteNews(id);
        fetchNews();
        setActionMenuOpen(null);
      } catch (err) {
        alert('Failed to delete news article');
      }
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    const actionText = status === 'published' ? 'publish' : 'archive';
    if (window.confirm(`Are you sure you want to ${actionText} ${selectedItems.length} news article(s)?`)) {
      try {
        await bulkUpdateNewsStatus(selectedItems, status);
        alert(`${selectedItems.length} news article(s) ${status === 'published' ? 'published' : 'archived'} successfully`);
        setSelectedItems([]);
        fetchNews();
      } catch (err) {
        alert('Failed to update news articles');
        console.error('Bulk status update error:', err);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectedItems.length} news article(s)?`)) {
      try {
        await bulkDeleteNews(selectedItems);
        alert(`${selectedItems.length} news article(s) deleted successfully`);
        setSelectedItems([]);
        fetchNews();
      } catch (err) {
        alert('Failed to delete news articles');
        console.error('Bulk delete error:', err);
      }
    }
  };

  const handleEdit = (id) => {
    navigate(`/admin/news/edit/${id}`);
    setActionMenuOpen(null);
  };

  const handleView = (id) => {
    navigate(`/news/${id}`);
    setActionMenuOpen(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, ',');
  };

  const totalPages = Math.ceil(totalNews / itemsPerPage);

  return (
    <AdminLayout>
      <div className="news-listing-content">
        {/* Header - NO white box, on grey background */}
        <div className="nl-page-header">
          <div className="nl-page-title-section">
            <h1 className="nl-page-title">News Listing</h1>
            <p className="nl-page-description">Manage your news and grow your business — all in one place.</p>
          </div>
          <div className="nl-header-actions">
            {selectedItems.length > 0 && (
              <>
                <button className="nl-bulk-btn publish" onClick={() => handleBulkStatusUpdate('published')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Publish ({selectedItems.length})
                </button>
                <button className="nl-bulk-btn archive" onClick={() => handleBulkStatusUpdate('archived')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M21 8v13H3V8M1 3h22v5H1zM10 12h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Archive ({selectedItems.length})
                </button>
                <button className="nl-bulk-btn delete" onClick={handleBulkDelete}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 6h18M8 6V4c0-1.1.9-2 2-2h4c1.1 0 2 .9 2 2v2m3 0v14c0 1.1-.9 2-2 2H7c-1.1 0-2-.9-2-2V6h14z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Delete ({selectedItems.length})
                </button>
              </>
            )}
            <button className="nl-create-btn" onClick={() => navigate('/admin/news/add')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Create News</span>
            </button>
          </div>
        </div>

        {/* Content Box - WHITE BOX with filter + table */}
        <div className="nl-content">
          <div className="nl-list-header">
            <h2 className="nl-list-title">News Lists</h2>
            <div className="nl-list-controls">
              <div className="nl-search-box">
                <input 
                  type="text" 
                  placeholder="Search..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="9.16667" cy="9.16667" r="5.83333" stroke="#969696" strokeWidth="1.5"/>
                  <path d="M14.166 14.1665L17.4993 17.4998" stroke="#969696" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              
              {/* Category Filter - Native Select */}
              <div className="nl-select-wrapper">
                <select 
                  className="nl-filter-select"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">Category</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              {/* Status Filter - Native Select */}
              <div className="nl-select-wrapper">
                <select 
                  className="nl-filter-select"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <option value="">Status</option>
                  {statuses.map(status => (
                    <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                  ))}
                </select>
              </div>
              
              {/* Filter By Sub Category - Native Select */}
              <div className="nl-select-wrapper nl-category-select">
                <svg className="nl-filter-icon" width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M18.0171 3.86955C17.9209 3.64729 17.7615 3.45822 17.5587 3.32584C17.3559 3.19347 17.1186 3.12363 16.8764 3.12502H3.12644C2.88452 3.1255 2.64792 3.19617 2.44537 3.32846C2.24282 3.46076 2.08301 3.64899 1.98532 3.87032C1.88764 4.09165 1.85629 4.33658 1.89507 4.57538C1.93385 4.81418 2.04109 5.03659 2.20379 5.21565L2.21004 5.22268L7.50144 10.8727V16.875C7.50139 17.1013 7.56274 17.3233 7.67894 17.5174C7.79514 17.7115 7.96185 17.8704 8.16128 17.9772C8.36071 18.084 8.58539 18.1347 8.81136 18.1239C9.03733 18.113 9.25613 18.0411 9.44441 17.9156L11.9444 16.2485C12.1158 16.1343 12.2563 15.9796 12.3535 15.798C12.4507 15.6165 12.5015 15.4137 12.5014 15.2078V10.8727L17.7936 5.22268L17.7999 5.21565C17.9643 5.03741 18.0726 4.8147 18.1112 4.57531C18.1498 4.33592 18.1171 4.09045 18.0171 3.86955Z" fill="#333333"/>
                </svg>
                <select 
                  className="nl-filter-select with-icon"
                  value={selectedSubCategory}
                  onChange={(e) => setSelectedSubCategory(e.target.value)}
                >
                  <option value="">Filter By Sub Category</option>
                  <option value="BIM">BIM</option>
                  <option value="Smart Buildings">Smart Buildings</option>
                  <option value="Roads">Roads</option>
                  <option value="Airports">Airports</option>
                  <option value="Equipment Safety">Equipment Safety</option>
                  <option value="Residential">Residential</option>
                  <option value="Energy Efficiency">Energy Efficiency</option>
                  <option value="Wood">Wood</option>
                  <option value="Market Updates">Market Updates</option>
                </select>
              </div>
              
              {(searchQuery || selectedCategory || selectedStatus || selectedSubCategory) && (
                <button className="nl-clear-btn" onClick={() => { setSearchQuery(''); setSelectedCategory(''); setSelectedStatus(''); setSelectedSubCategory(''); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Clear
                </button>
              )}
            </div>
          </div>
          
          {loading ? (
            <div className="nl-loading">Loading news...</div>
          ) : error ? (
            <div className="nl-error">{error}</div>
          ) : (
            <div className="nl-table-container">
              <div className="nl-table-wrapper">
                <div className="nl-table">
                  <div className="nl-table-header">
                    <div className="nl-th nl-th-checkbox">
                      <input 
                        type="checkbox"
                        checked={selectedItems.length === newsData.length && newsData.length > 0}
                        onChange={handleSelectAll}
                      />
                      <span>All</span>
                    </div>
                    <div className="nl-th">Image</div>
                    <div className="nl-th">Post Date</div>
                    <div className="nl-th">Listing Name</div>
                    <div className="nl-th nl-th-left">Category</div>
                    <div className="nl-th nl-th-left">Sub Category</div>
                    <div className="nl-th">Viewed</div>
                    <div className="nl-th">Status</div>
                    <div className="nl-th">Details</div>
                    <div className="nl-th">Action</div>
                  </div>

                  <div className="nl-table-body">
                    {newsData.length === 0 ? (
                      <div className="nl-no-data">No news articles found</div>
                    ) : (
                      newsData.map((news) => (
                        <div key={news.id} className="nl-table-row">
                          <div className="nl-td nl-td-checkbox">
                            <input 
                              type="checkbox"
                              checked={selectedItems.includes(news.id)}
                              onChange={() => handleSelectItem(news.id)}
                            />
                          </div>
                          <div className="nl-td nl-td-image">
                            <div 
                              className={`nl-news-thumb ${!news.image ? 'nl-news-thumb-placeholder' : ''}`}
                              style={{ backgroundImage: news.image ? `url(${news.image})` : 'none' }}
                            >
                              {!news.image && <span className="nl-no-image-text">No Image</span>}
                            </div>
                          </div>
                          <div className="nl-td nl-td-date">{formatDate(news.createdAt)}</div>
                          <div className="nl-td nl-td-title">
                            {news.title ? (news.title.length > 25 ? news.title.substring(0, 25) + '...' : news.title) : '-'}
                          </div>
                          <div className="nl-td nl-td-category">
                            {news.category || '-'}
                          </div>
                          <div className="nl-td nl-td-subcategory">
                            {news.subCategory || news.sub_category || '-'}
                          </div>
                          <div className="nl-td nl-td-views">{news.views || 0}</div>
                          <div className="nl-td nl-td-status">
                            <span className={`nl-status-badge ${news.status?.toLowerCase() || 'draft'}`}>
                              {news.status?.charAt(0).toUpperCase() + news.status?.slice(1) || 'Draft'}
                            </span>
                          </div>
                          <div className="nl-td nl-td-details">
                            <button 
                              className="nl-details-btn"
                              onClick={() => handleView(news.id)}
                            >
                              Details
                            </button>
                          </div>
                          <div className="nl-td nl-td-action">
                            <button 
                              className="nl-action-btn"
                              onClick={() => setActionMenuOpen(actionMenuOpen === news.id ? null : news.id)}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="5" r="1.5" fill="currentColor"/>
                                <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
                                <circle cx="12" cy="19" r="1.5" fill="currentColor"/>
                              </svg>
                            </button>
                            {actionMenuOpen === news.id && (
                              <div className="nl-action-menu">
                                <button onClick={() => handleView(news.id)}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <circle cx="12" cy="12" r="3" stroke="#333" strokeWidth="2"/>
                                  </svg>
                                  View
                                </button>
                                <button onClick={() => handleEdit(news.id)}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                  Edit
                                </button>
                                <button onClick={() => handleDelete(news.id)} className="delete">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Pagination inside table container */}
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default NewsListingPage;
